import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const CARD_SX = {
  backgroundColor: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: 3,
  p: 2.5,
  minHeight: 72,
} as const;

export type RouletteContentKpiData = {
  activeUsers: { value: number; delta: { text: string; isUp: boolean } };
  transactionAndUser: {
    txn: number;
    user: number;
    txnDelta: { text: string; isUp: boolean };
    userDelta: { text: string; isUp: boolean };
  };
  participationRate: { value: number; delta: { text: string; isUp: boolean } };
  avgTicket: { value: number; prev: number };
  maxTicket: {
    amount: number | string;
    rate: number;
    prevAmount: number | string;
    prevRate: number;
  };
  minTicket: {
    amount: number | string;
    rate: number;
    prevAmount: number | string;
    prevRate: number;
  };
};

type Props = {
  data?: RouletteContentKpiData | null;
  loading?: boolean;
  /** 홈 방문/랜덤 광고/영상 광고 탭에서는 false */
  showTicketKpis?: boolean;
  /** 룰렛 탭일 때 true -> 참여 사용자 수, 가장 많은/적은 룰렛 참여 3개만 표시, 단위 '룰렛' */
  isRouletteTab?: boolean;
  /** 툴팁에 넣을 콘텐츠명 (탭별로 전달, 예: 튜브낚시, 뜰채 낚시) */
  contentName?: string;
};

function formatRouletteAmount(amount: number | string): string {
  const s = String(amount);
  return s.endsWith('룰렛') ? s : `${s}룰렛`;
}

const DEFAULT_CONTENT_NAME = '해당 콘텐츠';

export default function RouletteContentKpiCards({
  data,
  loading = false,
  showTicketKpis = true,
  isRouletteTab = false,
  contentName,
}: Props) {
  const name = contentName ?? DEFAULT_CONTENT_NAME;
  const tooltipActiveUsers = '조회된 기간 동안 룰렛 메인에 접속한 사용자 수 입니다. (중복 제외)';
  const tooltipTxnCount = `조회된 기간 동안 ${name}에서 티켓을 적립 받은 사용자 수 입니다. (중복 포함)`;
  const tooltipTxnUser = `조회된 기간 동안 ${name}에서 티켓을 적립 받은 고유 사용자 수 입니다.`;

  if (isRouletteTab) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 2,
          mt: 3,
        }}
      >
        <Box sx={CARD_SX}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary" fontWeight="bold">
              참여 사용자 수
            </Typography>
          </Box>
          <Typography variant="h6" fontWeight="bold" color="#374151">
            {data ? `${data.transactionAndUser.user.toLocaleString('ko-KR')}명` : '—'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: '#64748b' }}>
            {data ? (
              <>
                전월 동기간 대비{' '}
                <Box
                  component="span"
                  sx={{
                    color: data.transactionAndUser.userDelta.isUp ? '#D32F2F' : '#1976D2',
                    fontWeight: 600,
                  }}
                >
                  {data.transactionAndUser.userDelta.text}
                </Box>
              </>
            ) : (
              '—'
            )}
          </Typography>
        </Box>
        <Box sx={CARD_SX}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary" fontWeight="bold">
              가장 많은 룰렛 참여
            </Typography>
          </Box>
          <Typography variant="h6" fontWeight="bold" color="#374151">
            {data
              ? `${formatRouletteAmount(data.maxTicket.amount)} (${data.maxTicket.rate}%)`
              : '—'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: '#64748b' }}>
            {data
              ? `전월 동기간 ${formatRouletteAmount(data.maxTicket.prevAmount)} ${data.maxTicket.prevRate}%`
              : '—'}
          </Typography>
        </Box>
        <Box sx={CARD_SX}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary" fontWeight="bold">
              가장 적은 룰렛 참여
            </Typography>
          </Box>
          <Typography variant="h6" fontWeight="bold" color="#374151">
            {data
              ? `${formatRouletteAmount(data.minTicket.amount)} (${data.minTicket.rate}%)`
              : '—'}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: '#64748b' }}>
            {data
              ? `전월 동기간 ${formatRouletteAmount(data.minTicket.prevAmount)} ${data.minTicket.prevRate}%`
              : '—'}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: showTicketKpis ? 'repeat(3, 1fr)' : 'repeat(3, 1fr)',
          lg: showTicketKpis ? 'repeat(6, 1fr)' : 'repeat(3, 1fr)',
        },
        gap: 2,
        mt: 3,
      }}
    >
      <Box sx={CARD_SX}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary" fontWeight="bold">
            활성 사용자 수
          </Typography>
          <Tooltip title={tooltipActiveUsers} arrow placement="top">
            <IconButton
              size="small"
              sx={{ p: 0, color: '#64748b' }}
              aria-label="활성 사용자 수 설명"
            >
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="h6" fontWeight="bold" color="#374151">
          {data ? `${data.activeUsers.value.toLocaleString('ko-KR')}명` : '—'}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: '#64748b' }}>
          {data ? (
            <>
              전월 동기간 대비{' '}
              <Box
                component="span"
                sx={{ color: data.activeUsers.delta.isUp ? '#D32F2F' : '#1976D2', fontWeight: 600 }}
              >
                {data.activeUsers.delta.text}
              </Box>
            </>
          ) : (
            '—'
          )}
        </Typography>
      </Box>

      <Box sx={CARD_SX}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
          <Typography component="span" variant="body2" color="text.secondary" fontWeight="bold">
            참여 완료 횟수
          </Typography>
          <Tooltip title={tooltipTxnCount} arrow placement="top">
            <IconButton
              size="small"
              sx={{ p: 0, color: '#64748b' }}
              aria-label="참여 완료 횟수 설명"
            >
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Typography component="span" variant="body2" color="text.secondary" fontWeight="bold">
            {' '}
            / 사용자 수
          </Typography>
          <Tooltip title={tooltipTxnUser} arrow placement="top">
            <IconButton size="small" sx={{ p: 0, color: '#64748b' }} aria-label="사용자 수 설명">
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="h6" fontWeight="bold" color="#374151">
          {data
            ? `${data.transactionAndUser.txn.toLocaleString('ko-KR')}회 / ${data.transactionAndUser.user.toLocaleString('ko-KR')}명`
            : '—'}
        </Typography>
        {data ? (
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
              전월 동기간 대비
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
              <Box
                component="span"
                sx={{
                  color: data.transactionAndUser.txnDelta.isUp ? '#D32F2F' : '#1976D2',
                  fontWeight: 600,
                }}
              >
                {data.transactionAndUser.txnDelta.text}
              </Box>
              {' / '}
              <Box
                component="span"
                sx={{
                  color: data.transactionAndUser.userDelta.isUp ? '#D32F2F' : '#1976D2',
                  fontWeight: 600,
                }}
              >
                {data.transactionAndUser.userDelta.text}
              </Box>
            </Typography>
          </Box>
        ) : null}
      </Box>

      <Box sx={CARD_SX}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary" fontWeight="bold">
            참여율
          </Typography>
          <Tooltip title="참여 완료 수 / 활성 사용자 수로 계산된 값입니다." arrow placement="top">
            <IconButton size="small" sx={{ p: 0, color: '#64748b' }} aria-label="참여율 설명">
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="h6" fontWeight="bold" color="#374151">
          {data ? `${data.participationRate.value}%` : '—'}
        </Typography>
        {data ? (
          <Box sx={{ mt: 0.5 }}>
            <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
              전월 동기간 대비
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
              <Box
                component="span"
                sx={{
                  color: data.participationRate.delta.isUp ? '#D32F2F' : '#1976D2',
                  fontWeight: 600,
                }}
              >
                {data.participationRate.delta.text}
              </Box>
            </Typography>
          </Box>
        ) : null}
      </Box>

      {showTicketKpis && (
        <>
          <Box sx={CARD_SX}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary" fontWeight="bold">
                획득 평균 티켓
              </Typography>
            </Box>
            <Typography variant="h6" fontWeight="bold" color="#374151">
              {data ? `${data.avgTicket.value}티켓` : '—'}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: '#64748b' }}>
              {data ? `전월 동기간 ${data.avgTicket.prev}티켓` : '—'}
            </Typography>
          </Box>

          <Box sx={CARD_SX}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary" fontWeight="bold">
                가장 많은 획득 티켓
              </Typography>
            </Box>
            <Typography variant="h6" fontWeight="bold" color="#374151">
              {data ? `${data.maxTicket.amount}티켓 (${data.maxTicket.rate}%)` : '—'}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: '#64748b' }}>
              {data
                ? `전월 동기간 ${data.maxTicket.prevAmount}티켓 ${data.maxTicket.prevRate}%`
                : '—'}
            </Typography>
          </Box>

          <Box sx={CARD_SX}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary" fontWeight="bold">
                가장 적은 획득 티켓
              </Typography>
            </Box>
            <Typography variant="h6" fontWeight="bold" color="#374151">
              {data ? `${data.minTicket.amount}티켓 (${data.minTicket.rate}%)` : '—'}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: '#64748b' }}>
              {data
                ? `전월 동기간 ${data.minTicket.prevAmount}티켓 ${data.minTicket.prevRate}%`
                : '—'}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
}
